import axios from 'axios';

const RAW_API_GATEWAY_URL = import.meta.env.VITE_API_URL || 'https://testingdb.ev91riders.com/api';
const NORMALIZED_API_GATEWAY_URL = RAW_API_GATEWAY_URL.replace(/\/\/+$/, '');
const API_GATEWAY_URL = NORMALIZED_API_GATEWAY_URL.endsWith('/api/v1')
  ? NORMALIZED_API_GATEWAY_URL.slice(0, -3)
  : NORMALIZED_API_GATEWAY_URL.endsWith('/api')
  ? NORMALIZED_API_GATEWAY_URL
  : `${NORMALIZED_API_GATEWAY_URL}/api`;

const api = axios.create({
  baseURL: API_GATEWAY_URL,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  
  if (!token) {
    console.warn(
      "No auth token found in localStorage when making MIS API request"
    );
  } else {
    console.log("Auth token found and will be used for MIS API request");
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

export const misService = {
  getMIS: async (params?: any) => {
    const res = await api.get('/mis', { params });
    return res.data;
  },
  getMISById: async (id: string) => {
    const res = await api.get(`/mis/${id}`);
    return res.data;
  },
  createMIS: async (data: any) => {
    const res = await api.post('/mis', data);
    return res.data;
  },
  updateMIS: async (id: string, data: any) => {
    const res = await api.put(`/mis/${id}`, data);
    return res.data;
  },
  getEarningsByRider: async (riderPublicRiderId: string, params?: any) => {
    const res = await api.get(`/mis/earnings/${encodeURIComponent(riderPublicRiderId)}`, { params });
    return res.data;
  },
  getDeductionsByRider: async (riderPublicRiderId: string, params?: any) => {
    const res = await api.get(`/mis/deductions/${encodeURIComponent(riderPublicRiderId)}`, { params });
    return res.data;
  },
  bulkUpload: async (file: File, fromDate: string, toDate: string) => {
    const form = new FormData();
    form.append('file', file);
    form.append('fromDate', fromDate);
    form.append('toDate', toDate);
    // Also send legacy keys expected by some frontends: Fromdata / Todate
    form.append('Fromdata', fromDate);
    form.append('Todate', toDate);

    const res = await api.post('/mis/bulk-upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },

  // Export MIS data for given date range; returns an axios response with blob data
  exportMIS: async (params?: { fromDate?: string; toDate?: string; format?: string }) => {
    try {
      const res = await api.get('/mis/export', { params, responseType: 'blob' as any });
      return res;
    } catch (err: any) {
      // If gateway returns 404, try direct rider service (useful during local dev)
      if (err?.response?.status === 404) {
        const axios = (await import('axios')).default;
        const riderUrl = import.meta.env.VITE_RIDER_SERVICE_URL || 'http://localhost:4005';
        try {
          const direct = await axios.get(`${riderUrl}/api/v1/mis/export`, { params, responseType: 'arraybuffer' });
          return direct;
        } catch (err2: any) {
          if (err2?.response?.status === 404) {
            const error = new Error('There is no payout data for given dates');
            throw error;
          }
          throw err2;
        }
      }
      if (err?.response?.status === 404) {
        const error = new Error('There is no payout data for given dates');
        throw error;
      }
      throw err;
    }
  },

  exportSdWallet: async () => {
    try {
      const res = await api.get('/v1/security-deposit/export/wallet', {
        responseType: 'blob' as any,
      });
      return res;
    } catch (err: any) {
      if (err?.response?.status === 404) {
        const axios = (await import('axios')).default;
        const riderUrl = import.meta.env.VITE_RIDER_SERVICE_URL || 'http://localhost:4005';
        const direct = await axios.get(`${riderUrl}/api/v1/security-deposit/export/wallet`, {
          responseType: 'arraybuffer',
        });
        return direct;
      }
      throw err;
    }
  },

  // Get MIS history summary with status counts
  getMISHistorySummary: async (params?: { fromDate?: string; toDate?: string; sortBy?: string; sortOrder?: string }) => {
    const res = await api.get('/mis/mishistory/summary', { params });
    return res.data;
  },
};
